import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

interface MerchantItem {
  id: string;
  code: string;
  name: string;
  paymentChannelPoolName: string;
  bankCode: string;
  accountNo: string;
  accountName: string;
  status: string;
  syncedAt: string | null;
}

export function SubSellerMerchantList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['sub-seller', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[]; total: number }>('/sub-seller/merchants'),
  });

  const merchants = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && merchants.length === 0 && (
            <div className="p-6 text-gray-400">{t('merchants.subSellerEmptyHint')}</div>
          )}
          {merchants.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('merchants.code')}</TableHead>
                    <TableHead>{t('merchants.name')}</TableHead>
                    <TableHead>{t('merchants.paymentChannel')}</TableHead>
                    <TableHead>{t('merchants.accountNo')}</TableHead>
                    <TableHead>{t('merchants.status')}</TableHead>
                    <TableHead>{t('merchants.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-sm">{m.code}</TableCell>
                      <TableCell>{m.name}</TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {m.paymentChannelPoolName || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {m.accountNo ? `${m.bankCode} ${m.accountNo}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            m.status === 'active'
                              ? 'success'
                              : m.status === 'locked'
                                ? 'danger'
                                : 'default'
                          }
                        >
                          {t(`common.${m.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/sub-seller/merchants/${m.id}/stats`)}
                        >
                          {t('merchants.viewStats')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
